import { db } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";

const getRoleFromActionType = (actionType = "") => {
  if (actionType.startsWith("student_")) return "student";
  if (actionType.startsWith("tutor_")) return "tutor";
  if (actionType.startsWith("admin_")) return "admin";
  return null;
};

const getActorDetails = async (activity) => {
  let actorRole = activity.userRole || getRoleFromActionType(activity.actionType);
  let actorName = activity.metadata?.userName || activity.userName || "User";

  if ((!actorRole || actorName === "User") && activity.userId) {
    try {
      const userSnap = await getDoc(doc(db, "users", activity.userId));
      if (userSnap.exists()) {
        const userData = userSnap.data();
        actorRole = actorRole || userData.role || null;
        actorName = activity.metadata?.userName || userData.name || userData.email || actorName;
      }
    } catch (error) {
      console.warn("Unable to infer activity actor details:", error);
    }
  }

  return { actorRole, actorName };
};

const getAdminRouteForActivity = (activity) => {
  if (activity.metadata?.route) return activity.metadata.route;

  const type = activity.type || "";
  const actionType = activity.actionType || "";
  const action = activity.metadata?.action || "";

  if (type === "task" || actionType === "task_assigned" || action.includes("task")) {
    return "/tasks";
  }

  if (type === "leave" || action.includes("leave")) {
    return "/leaves";
  }

  if (type === "check_in" || type === "check_out" || type === "attendance") {
    return "/attendance";
  }

  if (type === "skill") {
    return activity.userRole === "tutor" ? "/tutors" : "/students";
  }

  if (type === "fee" || action.includes("fee") || action.includes("payment")) {
    return "/fees";
  }

  if (type === "salary_payment_recorded" || type === "salary") {
    return "/salary";
  }

  return "/dashboard";
};

const getTargetRouteForActivity = (activity) => {
  const targetAllowedRoutes = new Set(["/dashboard", "/tasks", "/leaves", "/profile", "/holidays"]);
  if (activity.metadata?.route && targetAllowedRoutes.has(activity.metadata.route)) {
    return activity.metadata.route;
  }

  const type = activity.type || "";
  const actionType = activity.actionType || "";
  const action = activity.metadata?.action || "";

  if (type === "task" || actionType === "task_assigned" || action.includes("task")) {
    return "/tasks";
  }

  if (type === "leave" || action.includes("leave")) {
    return "/leaves";
  }

  if (type === "fee" || action.includes("fee") || action.includes("payment")) {
    return "/dashboard";
  }

  if (type === "salary_payment_recorded" || type === "salary") {
    return "/dashboard";
  }

  return "/dashboard";
};

// Log activity for any user (admin, tutor, student)
export const logActivity = async (activity) => {
  try {
    const { actorRole, actorName } = await getActorDetails(activity);

    // Add activity to database
    await addDoc(collection(db, "activities"), {
      userId: activity.userId,
      targetUserId: activity.targetUserId || null, // Who the action affects (optional)
      text: activity.text,
      type: activity.type,
      actionType: activity.actionType || "general", // admin_action, tutor_action, student_action
      userRole: actorRole || activity.userRole || null,
      createdAt: Date.now(),
      ...activity.metadata || {} // Additional data
    });

    // Notify admins if activity is from non-admin user
    if (actorRole && actorRole !== "admin") {
      try {
        const { createNotification } = await import("./notificationService");
        await createNotification({
          actorId: activity.userId,
          actorName,
          actorRole,
          title: activity.type.replace(/_/g, " ").toUpperCase(),
          message: activity.text,
          type: activity.type,
          actionType: activity.actionType,
          metadata: {
            ...(activity.metadata || {}),
            route: getAdminRouteForActivity({ ...activity, userRole: actorRole }),
          },
        });
      } catch (notifError) {
        console.warn("Failed to create notification:", notifError);
      }
    }

    // Notify relevant non-admin users if activity is from admin and targets them
    if (actorRole === "admin" && activity.targetUserId) {
      try {
        const { createNotification } = await import("./notificationService");
        await createNotification({
          actorId: activity.userId,
          actorName: actorName || "Admin",
          actorRole,
          title: activity.type.replace(/_/g, " ").toUpperCase(),
          message: activity.text,
          type: activity.type,
          actionType: activity.actionType,
          metadata: {
            ...(activity.metadata || {}),
            route: getTargetRouteForActivity(activity),
          },
          targetUserId: activity.targetUserId,
        });
      } catch (notifError) {
        console.warn("Failed to create notification for target user:", notifError);
      }
    }
  } catch (error) {
    console.error("Error in logActivity:", error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const logStudentActivity = async (activity) => {
  try {
    await logActivity({
      userId: activity.studentId,
      text: activity.text,
      type: activity.type,
      actionType: "student_action"
    });
  } catch (error) {
    throw error;
  }
};

// Get activities for a specific user (their own activities)
export const getUserActivities = async (userId, limit = 10) => {
  try {
    const q = query(
      collection(db, "activities"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    const activities = snapshot.docs.slice(0, limit).map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return activities;
  } catch (error) {
    console.error("Error fetching user activities:", error);
    throw error;
  }
};

// Get activities relevant to a user (actions that affect them by others)
export const getRelevantActivities = async (userId, userRole, limit = 10) => {
  try {
    // Validate required parameters
    if (!userId || !userRole) {
      console.warn("getRelevantActivities called with missing userId or userRole");
      return [];
    }

    // Get all users to create a lookup map
    const usersSnapshot = await getDocs(collection(db, "users"));
    const userMap = new Map();

    usersSnapshot.docs.forEach((doc) => {
      const userData = doc.data();
      userMap.set(doc.id, {
        name: userData.name || "Unknown User",
        role: userData.role || "Unknown",
        email: userData.email || "",
      });
    });

    let activities = [];

    if (userRole === "admin") {
      // Admin sees activities from students and tutors
      const q = query(
        collection(db, "activities"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const activityData = docSnap.data();
        const actorUser = userMap.get(activityData.userId);
        // Only include activities from non-admin users
        if (actorUser && actorUser.role !== "admin") {
          activities.push({
            id: docSnap.id,
            ...activityData,
            userName: actorUser.name,
            userRole: actorUser.role,
            userEmail: actorUser.email,
          });
          if (activities.length >= limit) break;
        }
      }
    } else {
      // Non-admins see:
      // 1. Admin actions that target them (leave approval/rejection, assignments)
      // 2. Tutor actions that target them
      // 3. Their own activities
      const q = query(
        collection(db, "activities"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const activityData = docSnap.data();
        const actorUser = userMap.get(activityData.userId);
        // Admin/tutor actions that target this user
        if (
          actorUser &&
          (actorUser.role === "admin" || actorUser.role === "tutor") &&
          activityData.targetUserId === userId
        ) {
          activities.push({
            id: docSnap.id,
            ...activityData,
            userName: actorUser.name,
            userRole: actorUser.role,
            userEmail: actorUser.email,
          });
        }
        // Their own activities
        else if (activityData.userId === userId) {
          activities.push({
            id: docSnap.id,
            ...activityData,
            userName: actorUser ? actorUser.name : "Unknown",
            userRole: actorUser ? actorUser.role : "Unknown",
            userEmail: actorUser ? actorUser.email : "",
          });
        }
        if (activities.length >= limit) break;
      }
    }

    return activities;
  } catch (error) {
    console.error("Error fetching relevant activities:", error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const getAdminActivities = async (studentId) => {
  return getUserActivities(studentId);
};

// Legacy function for backward compatibility  
export const getRecentUserActivities = async (limit = 7) => {
  try {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const userMap = new Map();

    usersSnapshot.docs.forEach((doc) => {
      const userData = doc.data();
      userMap.set(doc.id, {
        name: userData.name || "Unknown User",
        role: userData.role || "Unknown",
        email: userData.email || "",
      });
    });

    const q = query(
      collection(db, "activities"),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    const activities = [];

    for (const docSnap of snapshot.docs) {
      const activityData = docSnap.data();
      const user = userMap.get(activityData.userId);

      if (user && user.role !== "admin") {
        activities.push({
          id: docSnap.id,
          ...activityData,
          userName: user.name,
          userRole: user.role,
          userEmail: user.email,
        });

        if (activities.length >= limit) break;
      }
    }

    return activities;
  } catch (error) {
    console.error("Error fetching recent user activities:", error);
    throw error;
  }
};

export const clearOldActivities = async (userId) => {
  try {
    // Calculate timestamp for one week ago
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    // Query for activities older than one week for the specific user
    const q = query(
      collection(db, "activities"),
      where("userId", "==", userId),
      where("createdAt", "<", oneWeekAgo)
    );

    const snapshot = await getDocs(q);

    // Delete each old activity
    const deletePromises = snapshot.docs.map((docSnapshot) =>
      deleteDoc(doc(db, "activities", docSnapshot.id))
    );

    await Promise.all(deletePromises);

    return {
      success: true,
      deletedCount: snapshot.docs.length,
      message: `Cleared ${snapshot.docs.length} old activities for user ${userId}`,
    };
  } catch (error) {
    console.error("Error clearing old activities:", error);
    throw error;
  }
};

export const clearAllOldActivities = async () => {
  try {
    // Calculate timestamp for one week ago
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    // Query for all activities older than one week
    const q = query(
      collection(db, "activities"),
      where("createdAt", "<", oneWeekAgo)
    );

    const snapshot = await getDocs(q);

    // Delete each old activity
    const deletePromises = snapshot.docs.map((docSnapshot) =>
      deleteDoc(doc(db, "activities", docSnapshot.id))
    );

    await Promise.all(deletePromises);

    return {
      success: true,
      deletedCount: snapshot.docs.length,
      message: `Cleared ${snapshot.docs.length} old activities from all users`,
    };
  } catch (error) {
    console.error("Error clearing all old activities:", error);
    throw error;
  }
};
